
import { 
  ModelBase 
} from "@models/UpsertableBase";
import { 
  IStringNumHash,
  FeatureBase,
} from "@models";
import { SpellcastingFeature, TemplateBase } from "@dndModels";
import { AttackType, DamageType, DataType, EffectType, FeatureType } from "@dndModels/Types";

export class FeatureSource extends ModelBase {
  data_type: DataType = "feature_source";
  feature_source_type = "FeatureSource";
  feature_bases: FeatureBase[] = [];

  constructor(obj?: any, fs_type?: string) {
    super(obj);
    this.feature_source_type = fs_type ? fs_type : "FeatureSource";
    if (obj) {
      this.feature_bases = [];
      if (obj.feature_bases && obj.feature_bases.length > 0) {
        for (const o of obj.feature_bases) {
          o.source_id = this._id;
          o.source_type = this.feature_source_type;
          const fb = new FeatureBase(o);
          this.feature_bases.push(fb);
        }
      }
      this._damage_types = obj._damage_types ? obj._damage_types : [];
      this._the_damage_types = obj._the_damage_types ? obj._the_damage_types : [];
      this._the_attack_types = obj._the_attack_types ? obj._the_attack_types : [];
      this._the_effect_types = obj._the_effect_types ? obj._the_effect_types : [];
      this._feature_types = obj._feature_types ? obj._feature_types : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     // debugger;
  //     this.feature_bases = [];
  //     if (obj.feature_bases && obj.feature_bases.length > 0) {
  //       for (const o of obj.feature_bases) {
  //         o.source_id = this._id;
  //         o.source_type = this.feature_source_type;
  //         const fb = new FeatureBase(o);
  //         this.feature_bases.push(fb);
  //       }
  //     }
  //   }
  // }

  _feature_types: FeatureType[] = [];
  get feature_types() {
    return this.get_feature_types();
  }
  get_feature_types() {
    if (this.loaded) {
      return this.feature_bases.flatMap(o => o.features.map(o => o.feature_type));
    } else {
      return this._feature_types;
    }
  }

  getFBsForFeaturesOfType(feature_type: FeatureType) {
    return this.feature_bases.filter(o => o.feature_types.includes(feature_type));
  }

  getFeaturesOfType(feature_type: FeatureType) {
    return this.getFBsForFeaturesOfType(feature_type).flatMap(o => o.features.filter(o2 => o2.feature_type === feature_type));
  }

  getSpellcastingFeature() {
    const fs = this.getFeaturesOfType("Spellcasting");
    if (fs.length === 1 && fs[0].floating_feature instanceof SpellcastingFeature) {
      return fs[0].floating_feature;
    }
    return undefined;
  }

  _the_effect_types: EffectType[] = [];
  get the_effect_types() {
    return this.get_the_effect_types();
  }
  get_the_effect_types() {
    if (this.loaded) {
      return this.feature_bases.flatMap(o => o.the_effect_types);
    } else {
      return this._the_effect_types;
    }
  }

  _the_attack_types: AttackType[] = [];
  get the_attack_types() {
    return this.get_the_attack_types();
  }
  get_the_attack_types() {
    if (this.loaded) {
      return this.feature_bases.flatMap(o => o.the_attack_types);
    } else {
      return this._the_attack_types;
    }
  }
  
  _damage_types: DamageType[] = [];
  get damage_types() {
    return this.get_damage_types();
  }
  get_damage_types() {
    if (this.loaded) {
      return this.feature_bases.flatMap(o => o.damage_types);
    } else {
      return this._damage_types;
    }
  }
  
  _the_damage_types: (DamageType | "Selectable" | "Unarmed" | "Selectable Key" | "Use Extra")[] = [];
  get the_damage_types() {
    return this.get_the_damage_types();
  }
  get_the_damage_types() {
    if (this.loaded) {
      return this.feature_bases.flatMap(o => o.damage_types);
    } else {
      return this._the_damage_types;
    }
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      feature_bases: this.feature_bases.map(o => o.toDBObj()),
    };
  }

  toLight() {
    return {
      ...super.toLight(),
      _damage_types: this.damage_types,
      _the_damage_types: this.the_damage_types,
      _the_attack_types: this.the_attack_types,
      _the_effect_types: this.the_effect_types,
      _feature_types: this.feature_types
    }
  }

  get total_feature_points(): number {
    return this.feature_bases.reduce((accumulator: number, fp: FeatureBase) => {
      return accumulator + fp.total_feature_points;
    }, 0);
  }

  get total_avg_feature_points(): number {
    return this.feature_bases.reduce((accumulator: number, fp: FeatureBase) => {
      return accumulator + fp.total_avg_feature_points;
    }, 0);
  }

  get total_feature_points_by_level(): IStringNumHash {
    const return_me: IStringNumHash = {};
    this.feature_bases.forEach(fb => {
      if (return_me[fb.level]) {
        return_me[fb.level] += fb.total_feature_points;
      } else {
        return_me[fb.level] = fb.total_feature_points;
      }
    });
    return return_me;
  }

  get total_avg_feature_points_by_level(): IStringNumHash {
    const return_me: IStringNumHash = {};
    this.feature_bases.forEach(fb => {
      if (return_me[fb.level]) {
        return_me[fb.level] += fb.total_avg_feature_points;
      } else {
        return_me[fb.level] = fb.total_avg_feature_points;
      }
    });
    return return_me;
  }

  copy(copyMe: FeatureSource, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.feature_source_type = copyMe.feature_source_type;
    this.feature_bases = copyMe.feature_bases.map(o => o.clone(drop_ids));
  }

  copyTemplate(copyMe: TemplateBase): void {
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.source_books_old = [];
    copyMe.source_books_old.forEach(sb => {
      this.source_books_old.push({...sb});
    });
    const obj = {...copyMe.item};
    this.feature_source_type = obj && obj.feature_source_type ? obj.feature_source_type : "FeatureSource";
    this.feature_bases = [];
    if (obj && obj.feature_bases && obj.feature_bases.length > 0) {
      obj.feature_bases.forEach((o: any) => {
        o.source_id = this._id;
        o.source_type = this.feature_source_type;
        const fb = new FeatureBase(o);
        this.feature_bases.push(fb);
      });
    }
  }

  specialCopy(copyMe: FeatureSource): void {
    this.name = copyMe.name + " (Special Copy)";
    this.description = copyMe.description;
    this.source_books_old = [];
    copyMe.source_books_old.forEach(sb => {
      this.source_books_old.push({...sb});
    });
    this.feature_bases = copyMe.feature_bases.map(o => { return o.clone(true); });
  }

  clone(drop_ids = false): FeatureSource {
    const new_obj = new FeatureSource();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}